import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Box } from "@mui/material";
import {
  DataGrid,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridCellParams,
  GridColDef,
  GridFooterContainerProps,
  GridRowParams,
  GridRowSelectionModel,
  GridSlotsComponent,
  GridSortDirection,
  GridValidRowModel,
} from "@mui/x-data-grid";

import { isFromNestedInteractiveElement } from "../utils/isFromNestedInteractiveElement";
import {
  DATA_TABLE_PAGE_SIZE,
  dataTableLocales,
  dataTableSortIconSlots,
  getDataTableChromeSx,
} from "./DataTable.utils";
import { DataTablePagination } from "./DataTablePagination";

interface TypedSortItem<T> {
  field: Extract<keyof T, string>;
  sort: GridSortDirection;
}

interface Props<T extends GridValidRowModel> {
  data: T[];
  columns: GridColDef[];
  isLoading?: boolean;
  getRowId: (row: T) => string;
  onRowClick?: (rowId: string) => void;
  rowHeight?: number;
  /** Grows each row to fit its content instead of the fixed {@link rowHeight}, e.g. for multi-line cells. */
  autoRowHeight?: boolean;
  columnHeaderHeight?: number;
  autoHeight?: boolean;
  disableColumnMenu?: boolean;
  checkboxSelection?: boolean;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
  hideSelectAllCheckbox?: boolean;
  /** Decides per row whether it carries a selection checkbox. Rows that return false cannot be selected. */
  isRowSelectable?: (params: GridRowParams<T>) => boolean;
  hideNoRowsOverlay?: boolean;
  initialSortModel?: Array<TypedSortItem<T>>;
  /** Own footer row (e.g. a totals line), rendered between the rows and the page control. */
  footerContent?: ReactNode;
  /**
   * Signature of the caller's active filters. Whenever it changes, the table goes back to page 1 so
   * a narrowed result set is shown from its start instead of a page number that no longer applies.
   */
  paginationResetKey?: string;
}

/** Grid footer: the caller's own footer row (if any), with the page control below it. */
function DataTableFooter({ children }: GridFooterContainerProps) {
  return (
    <>
      {children}
      <DataTablePagination />
    </>
  );
}

const dataTableSlots: Partial<GridSlotsComponent> = { ...dataTableSortIconSlots, footer: DataTableFooter };

const autoRowHeightOf = () => "auto" as const;

/** {@link dataTableSlots} with a no-op overlay, for grids that render nothing when empty. */
const dataTableSlotsWithoutNoRowsOverlay: Partial<GridSlotsComponent> = {
  ...dataTableSlots,
  noRowsOverlay: () => null,
};

export function DataTable<T extends GridValidRowModel>({
  data,
  columns,
  isLoading,
  getRowId,
  onRowClick,
  rowHeight = 81.5,
  autoRowHeight,
  columnHeaderHeight,
  autoHeight,
  disableColumnMenu,
  checkboxSelection,
  rowSelectionModel,
  onRowSelectionModelChange,
  hideSelectAllCheckbox,
  isRowSelectable,
  hideNoRowsOverlay,
  initialSortModel,
  footerContent,
  paginationResetKey,
}: Props<T>) {
  const { i18n } = useTranslation();
  // The selected page is stored together with the filter signature it was picked under. Whenever
  // the caller's filters change — even back to a previously seen value — the stored signature no
  // longer matches and the page is reset during render (the React-documented alternative to a
  // state-syncing effect), so every filter change lands on page 1.
  // Sorting needs no handler here: the grid resets the page on sortModelChange itself.
  const [pageState, setPageState] = useState({ page: 0, resetKey: paginationResetKey });
  if (pageState.resetKey !== paginationResetKey) {
    setPageState({ page: 0, resetKey: paginationResetKey });
  }

  const { page } = pageState;
  const paginationModel = useMemo(() => ({ page, pageSize: DATA_TABLE_PAGE_SIZE }), [page]);

  // The grid answers a click on a row but no key, so a row that opens something would be reachable by
  // mouse only. Enter on a focused cell therefore does what clicking it does — except where the cell's
  // own content already answers the key: the selection checkbox, or a link or button inside the cell,
  // which would otherwise be followed and open the row on top of it.
  const handleCellKeyDown = (params: GridCellParams, event: React.KeyboardEvent<HTMLElement>) => {
    if (!onRowClick || event.key !== "Enter" || params.field === GRID_CHECKBOX_SELECTION_COL_DEF.field) {
      return;
    }

    if (isFromNestedInteractiveElement(event.target, event.currentTarget)) {
      return;
    }

    onRowClick(params.id.toString());
  };

  return (
    <Box sx={{ height: autoHeight ? undefined : "100%", width: "100%" }}>
      <DataGrid
        rows={data}
        columns={columns}
        loading={isLoading}
        sortingMode="client"
        initialState={initialSortModel ? { sorting: { sortModel: initialSortModel } } : undefined}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => setPageState({ page: model.page, resetKey: paginationResetKey })}
        pageSizeOptions={[DATA_TABLE_PAGE_SIZE]}
        autoHeight={autoHeight}
        getRowId={getRowId}
        onRowClick={onRowClick ? (params) => onRowClick(params.id.toString()) : undefined}
        onCellKeyDown={handleCellKeyDown}
        disableRowSelectionOnClick
        checkboxSelection={checkboxSelection}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
        isRowSelectable={isRowSelectable}
        slots={hideNoRowsOverlay ? dataTableSlotsWithoutNoRowsOverlay : dataTableSlots}
        slotProps={{ footer: { children: footerContent } }}
        getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row")}
        sx={(theme) => ({
          ...getDataTableChromeSx(theme, { clickableRows: !!onRowClick }),
          ...(autoRowHeight && {
            "& .MuiDataGrid-cell": {
              py: 2,
            },
          }),
          ...(hideSelectAllCheckbox && {
            "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": {
              display: "none",
            },
          }),
        })}
        columnHeaderHeight={columnHeaderHeight ?? rowHeight}
        rowHeight={rowHeight}
        getRowHeight={autoRowHeight ? autoRowHeightOf : undefined}
        disableColumnResize
        disableColumnSelector
        disableColumnMenu={disableColumnMenu}
        localeText={dataTableLocales[i18n.language]}
      />
    </Box>
  );
}
